import { api } from './client';

export interface VaultPreloginResponse {
  kdf: number;
  kdfIterations: number;
  kdfMemory?: number;
  kdfParallelism?: number;
}

export interface VaultLoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  Key: string; // Encrypted symmetric key
  PrivateKey: string; // Encrypted private key
  TwoFactorToken?: string; // Token for "remember device" bypass
}

// Response when 2FA is required
export interface VaultTwoFactorResponse {
  twoFactorRequired: true;
  twoFactorProviders: number[]; // 0=TOTP, 1=Email, 2=Duo, etc.
}

// Cipher types
export enum CipherType {
  Login = 1,
  SecureNote = 2,
  Card = 3,
  Identity = 4,
}

// Custom field types
export enum FieldType {
  Text = 0,
  Hidden = 1,
  Boolean = 2,
  Linked = 3,
}

export interface VaultCipherField {
  type: FieldType;
  name?: string;
  value?: string;
  linkedId?: number;
}

export interface VaultCipher {
  id: string;
  type: CipherType;
  name: string;
  notes?: string;
  organizationId?: string;

  // Login (type=1)
  login?: {
    username?: string;
    password?: string;
    totp?: string;
    uris?: { uri: string; match?: number }[];
  };

  // Secure Note (type=2)
  secureNote?: {
    type: 0;
  };

  // Card (type=3)
  card?: {
    cardholderName?: string;
    brand?: string;
    number?: string;
    expMonth?: string;
    expYear?: string;
    code?: string;
  };

  // Identity (type=4)
  identity?: {
    title?: string;
    firstName?: string;
    middleName?: string;
    lastName?: string;
    username?: string;
    company?: string;
    ssn?: string;
    passportNumber?: string;
    licenseNumber?: string;
    email?: string;
    phone?: string;
    address1?: string;
    address2?: string;
    address3?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };

  // Custom fields (all types)
  fields?: VaultCipherField[];

  collectionIds: string[];
}

export interface VaultCollection {
  id: string;
  name: string;
  organizationId: string;
}

export interface VaultOrganization {
  id: string;
  name: string;
  key: string;
}

export interface VaultSyncResponse {
  ciphers: VaultCipher[];
  collections: VaultCollection[];
  profile: {
    id: string;
    email: string;
    organizations: VaultOrganization[];
  };
}

export const vaultApi = {
  /**
   * Check if vault service is available
   */
  getStatus: () => api.get<{ available: boolean }>('/vault/status').then((r) => r.data),

  /**
   * Get KDF parameters for login
   */
  prelogin: (email: string) =>
    api.post<VaultPreloginResponse>('/vault/prelogin', { email }).then((r) => r.data),

  /**
   * Login to vault
   * Returns VaultLoginResponse on success, VaultTwoFactorResponse if 2FA required
   */
  login: (
    email: string,
    masterPasswordHash: string,
    twoFactorToken?: string,
    twoFactorProvider?: number,
    twoFactorRemember?: boolean,
  ) =>
    api
      .post<VaultLoginResponse | VaultTwoFactorResponse>('/vault/login', {
        email,
        masterPasswordHash,
        twoFactorToken,
        twoFactorProvider,
        twoFactorRemember,
      })
      .then((r) => r.data),

  /**
   * Sync vault data (requires vault token)
   */
  sync: (vaultToken: string) =>
    api
      .get<VaultSyncResponse>('/vault/sync', {
        headers: { 'X-Vault-Token': vaultToken },
      })
      .then((r) => r.data),

  /**
   * Create a new collection in an organization
   * Name must be encrypted with org key
   */
  createCollection: (vaultToken: string, organizationId: string, encryptedName: string) =>
    api
      .post<{ id: string; name: string }>(
        '/vault/collections',
        { organizationId, name: encryptedName },
        { headers: { 'X-Vault-Token': vaultToken } },
      )
      .then((r) => r.data),

  /**
   * Create a new cipher
   * All fields must be encrypted with org key
   * Supports all types: Login (1), SecureNote (2), Card (3), Identity (4)
   */
  createCipher: (
    vaultToken: string,
    data: {
      type: CipherType;
      organizationId: string;
      collectionIds: string[];
      name: string;
      notes?: string;
      login?: {
        username?: string;
        password?: string;
        totp?: string;
        uris?: { uri: string; match?: number }[];
      };
      card?: {
        cardholderName?: string;
        brand?: string;
        number?: string;
        expMonth?: string;
        expYear?: string;
        code?: string;
      };
      identity?: {
        title?: string;
        firstName?: string;
        middleName?: string;
        lastName?: string;
        username?: string;
        company?: string;
        ssn?: string;
        passportNumber?: string;
        licenseNumber?: string;
        email?: string;
        phone?: string;
        address1?: string;
        address2?: string;
        address3?: string;
        city?: string;
        state?: string;
        postalCode?: string;
        country?: string;
      };
      fields?: { type: FieldType; name: string; value: string; linkedId?: number }[];
    },
  ) =>
    api
      .post<{ id: string }>('/vault/ciphers', data, {
        headers: { 'X-Vault-Token': vaultToken },
      })
      .then((r) => r.data),

  /**
   * Create a Bitwarden Send (text type)
   */
  createSend: (vaultToken: string, data: Record<string, unknown>) =>
    api
      .post<Record<string, unknown>>('/vault/sends', data, {
        headers: { 'X-Vault-Token': vaultToken },
      })
      .then((r) => r.data),

  /**
   * List user's Sends
   */
  listSends: (vaultToken: string) =>
    api
      .get<Record<string, unknown>[]>('/vault/sends', {
        headers: { 'X-Vault-Token': vaultToken },
      })
      .then((r) => r.data),

  /**
   * Delete a Send
   */
  deleteSend: (vaultToken: string, sendId: string) =>
    api
      .delete('/vault/sends/' + sendId, {
        headers: { 'X-Vault-Token': vaultToken },
      })
      .then((r) => r.data),

  /**
   * Update an existing cipher
   * All fields must be encrypted with org key
   */
  updateCipher: (
    vaultToken: string,
    cipherId: string,
    data: {
      type: CipherType;
      organizationId: string;
      collectionIds: string[];
      name: string;
      notes?: string;
      login?: {
        username?: string;
        password?: string;
        totp?: string;
        uris?: { uri: string; match?: number }[];
      };
      card?: {
        cardholderName?: string;
        brand?: string;
        number?: string;
        expMonth?: string;
        expYear?: string;
        code?: string;
      };
      identity?: {
        title?: string;
        firstName?: string;
        middleName?: string;
        lastName?: string;
        username?: string;
        company?: string;
        ssn?: string;
        passportNumber?: string;
        licenseNumber?: string;
        email?: string;
        phone?: string;
        address1?: string;
        address2?: string;
        address3?: string;
        city?: string;
        state?: string;
        postalCode?: string;
        country?: string;
      };
      fields?: { type: FieldType; name: string; value: string; linkedId?: number }[];
    },
  ) =>
    api
      .put<{ id: string }>(`/vault/ciphers/${cipherId}`, data, {
        headers: { 'X-Vault-Token': vaultToken },
      })
      .then((r) => r.data),
};
